import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Grid,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { BusinessContext } from '../context/BusinessContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PerformanceAnalysisChart from '../components/PerformanceAnalysisChart';

const SalesData = () => {
  const { selectedBusinesses, currencySymbol } = useContext(BusinessContext);
  const [salesPeriods, setSalesPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [periodFormData, setPeriodFormData] = useState({
    period_start: null,
    period_end: null,
    period_type: 'monthly',
    notes: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);

  // Sample CSV format for user reference
  const csvTemplate = `category,subject,unit_price,qty_sold,unit_gross_margin
Food,Pizza,15.99,120,8.50
Food,Burgers,12.99,85,6.25
Service,Wait Time,,,
Ambiance,Music,,,`;

  useEffect(() => {
    if (selectedBusinesses.length > 0) {
      fetchSalesPeriods();
    }
  }, [selectedBusinesses]);

  const fetchSalesPeriods = async () => {
    if (selectedBusinesses.length === 0) return;
    
    setLoading(true);
    try {
      const businessId = selectedBusinesses[0].id;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sales/periods?business_id=${businessId}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSalesPeriods(data);
        setError(null);
      } else {
        setError('Failed to fetch sales periods');
      }
    } catch (err) {
      setError('Error fetching sales periods');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesData = async (periodId) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sales/periods/${periodId}/data`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSalesData(data);
        setError(null);
      } else {
        setError('Failed to fetch sales data');
      }
    } catch (err) {
      setError('Error fetching sales data');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodClick = (period) => {
    setSelectedPeriod(period);
    fetchSalesData(period.id);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || selectedBusinesses.length === 0) {
      setError('Please select a business and CSV file');
      return;
    }

    if (!periodFormData.period_start || !periodFormData.period_end) {
      setError('Please select start and end dates');
      return;
    }

    setUploadProgress(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('business_id', selectedBusinesses[0].id);
      formData.append('period_start', periodFormData.period_start.format('YYYY-MM-DD'));
      formData.append('period_end', periodFormData.period_end.format('YYYY-MM-DD'));
      formData.append('period_type', periodFormData.period_type);
      formData.append('notes', periodFormData.notes);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sales/upload-csv`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      if (response.ok) {
        const result = await response.json();
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setPeriodFormData({
          period_start: null,
          period_end: null,
          period_type: 'monthly',
          notes: ''
        });
        fetchSalesPeriods();
        setError(null);
      } else {
        const errorData = await response.json();
        // Handle different error formats
        let errorMessage = 'Upload failed';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle Pydantic validation errors
            errorMessage = errorData.detail.map(err => 
              `${err.loc?.join('.')}: ${err.msg}`
            ).join(', ');
          }
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError('Error uploading file');
      console.error('Error:', err);
    } finally {
      setUploadProgress(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `${currencySymbol}${Number(amount).toLocaleString('en-GB', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const calculateTotals = () => {
    if (!salesData.length) return { revenue: 0, profit: 0, items: 0 };
    
    return salesData.reduce((totals, item) => ({
      revenue: totals.revenue + (parseFloat(item.total_revenue) || 0),
      profit: totals.profit + (parseFloat(item.total_gross_profit) || 0),
      items: totals.items + (parseInt(item.qty_sold) || 0)
    }), { revenue: 0, profit: 0, items: 0 });
  };

  const totals = calculateTotals();

  if (selectedBusinesses.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          Please select a business to view sales data.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Sales Data
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload and manage sales data for {selectedBusinesses[0].name}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Upload New Sales Data
            </Typography>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload CSV
            </Button>
          </Box>
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>CSV Format Requirements</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Your CSV file should have the following columns:
              </Typography>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
                  {csvTemplate}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                • <strong>category</strong>: Must match your defined categories<br />
                • <strong>subject</strong>: Must match your defined subjects<br />
                • <strong>unit_price</strong>: Price per unit (optional)<br />
                • <strong>qty_sold</strong>: Quantity sold (optional)<br />
                • <strong>unit_gross_margin</strong>: Gross margin per unit (optional)
              </Typography>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {selectedPeriod && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoneyIcon sx={{ mr: 1, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h6">{formatCurrency(totals.revenue)}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6">{formatCurrency(totals.profit)}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Profit</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AssessmentIcon sx={{ mr: 1, color: 'info.main' }} />
                  <Box>
                    <Typography variant="h6">{totals.items.toLocaleString()}</Typography>
                    <Typography variant="body2" color="text.secondary">Items Sold</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Sales Periods */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Sales Periods
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : salesPeriods.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No sales periods found. Upload your first CSV file to get started.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {salesPeriods.map((period) => (
                <Grid item xs={12} md={6} key={period.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer', 
                      border: selectedPeriod?.id === period.id ? 2 : 1,
                      borderColor: selectedPeriod?.id === period.id ? 'primary.main' : 'grey.300',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => handlePeriodClick(period)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1">
                            {dayjs(period.period_start).format('MMM YYYY')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {dayjs(period.period_start).format('MMM D')} - {dayjs(period.period_end).format('MMM D, YYYY')}
                          </Typography>
                          {period.notes && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {period.notes}
                            </Typography>
                          )}
                        </Box>
                        <Chip 
                          label={period.period_type} 
                          size="small"
                          color={period.period_type === 'monthly' ? 'primary' : 'secondary'}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Sales Data Table */}
      {selectedPeriod && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Sales Data for {dayjs(selectedPeriod.period_start).format('MMM YYYY')}
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : salesData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No sales data found for this period.
              </Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Qty Sold</TableCell>
                      <TableCell align="right">Unit Margin</TableCell>
                      <TableCell align="right">Total Revenue</TableCell>
                      <TableCell align="right">Total Profit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>{item.subject}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right">{item.qty_sold || 'N/A'}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_gross_margin)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.total_revenue)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.total_gross_profit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Performance Analysis Chart */}
      <PerformanceAnalysisChart selectedPeriod={selectedPeriod} />

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Sales Data</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Period Start"
                    value={periodFormData.period_start}
                    onChange={(date) => setPeriodFormData({ ...periodFormData, period_start: date })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Period End"
                    value={periodFormData.period_end}
                    onChange={(date) => setPeriodFormData({ ...periodFormData, period_end: date })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Period Type</InputLabel>
                  <Select
                    value={periodFormData.period_type}
                    label="Period Type"
                    onChange={(e) => setPeriodFormData({ ...periodFormData, period_type: e.target.value })}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Notes (optional)"
                  value={periodFormData.notes}
                  onChange={(e) => setPeriodFormData({ ...periodFormData, notes: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 2 }}
                  startIcon={<CloudUploadIcon />}
                >
                  {selectedFile ? selectedFile.name : 'Select CSV File'}
                  <input
                    type="file"
                    hidden
                    accept=".csv"
                    onChange={handleFileSelect}
                  />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploadProgress}
          >
            {uploadProgress ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SalesData; 